import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AnalyticsTopBar from "../components/AnalyticsTopBar";
import QueryBuilderPanel from "../components/QueryBuilderPanel";
import InsightsEmptyState from "../components/InsightsEmptyState";
import ReportLayout from "../../common/components/ReportLayout";
import InsightsChart from "../components/InsightsChart";
import InsightsDataTable from "../components/InsightsDataTable";
import MetricCards from "../components/MetricCards";
import FunnelVisualization from "../components/FunnelVisualization";
import FunnelLineChart from "../components/FunnelLineChart";
import FunnelSummaryBarChart from "../components/FunnelSummaryBarChart";
import FunnelBreakdownBarChart from "../components/FunnelBreakdownBarChart";
import FunnelMetricView from "../components/FunnelMetricView";
import FunnelDataTable from "../components/FunnelDataTable";
import RetentionVisualization from "../components/RetentionVisualization";
import RetentionMetricTable from "../components/RetentionMetricTable";
import EventsTimeFilter from "../../../conversion-events/components/EventsTimeFilter";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { useAnalytics } from "../context/AnalyticsContext";
import { useInsights, useEventsMetadata, useCards } from "../hooks/useInsights";
import { useBoards } from "../hooks/useBoards";
import * as analyticsApi from "../api/analyticsApi";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

/** Default chart config for funnels so axis options apply before panel syncs (matches QueryBuilderPanel defaults). */
const DEFAULT_FUNNEL_CHART_CONFIG = {
  axis: {
    leftY: { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" },
    rightY: { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" },
    xAxis: { visible: true, label: "" },
  },
  themeId: "mixpanel-default",
};

/** Map analytics preset (e.g. "30D") to EventsTimeFilter preset (e.g. "30d") */
function analyticsPresetToEventsPreset(analyticsPreset) {
  if (!analyticsPreset) return "30d";
  const p = String(analyticsPreset);
  if (p === "Today") return "today";
  if (p === "Yesterday") return "yesterday";
  if (p === "7D") return "7d";
  if (p === "30D") return "30d";
  if (p === "3M") return "3m";
  if (p === "6M") return "6m";
  if (p === "12M") return "12m";
  return "30d";
}

/**
 * Normalize custom date range to UTC calendar-day boundaries so the chart shows
 * data from the selected start date only (no previous day). EventsTimeFilter
 * sends local midnight (e.g. Jan 14 00:00 IST = 2026-01-13T18:30:00.000Z); we must
 * use the calendar date the user selected (local), not the UTC date from the ISO.
 * - start: selected start calendar date at 00:00:00.000Z
 * - end: day after selected end at 00:00:00.000Z (backend uses timestamp < end)
 */
function normalizeCustomRangeToUtcDays(fromIso, toIso, enableTimeRanges) {
  if (enableTimeRanges) {
    return { start: fromIso, end: toIso };
  }
  const toLocalYmd = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const fromYmd = toLocalYmd(fromIso);
  const toYmd = toLocalYmd(toIso);
  if (!fromYmd || !toYmd) return { start: fromIso, end: toIso };
  const nextDayYmd = (ymd) => {
    const d = new Date(ymd + "T12:00:00.000Z");
    d.setUTCDate(d.getUTCDate() + 1);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return {
    start: fromYmd + "T00:00:00.000Z",
    end: nextDayYmd(toYmd) + "T00:00:00.000Z",
  };
}

// Helper function to sort and limit funnel breakdowns (similar to insights)
function sortAndLimitFunnelBreakdowns(breakdowns, breakdownLimit, breakdownSortOrder, breakdownShowMax) {
  if (!breakdowns || !Array.isArray(breakdowns) || breakdowns.length === 0) {
    return [];
  }
  
  const maxLimit = breakdownShowMax ? 50 : Math.min(breakdownLimit, 50);
  
  // Calculate average for each breakdown (using step1 count as the metric)
  const breakdownsWithAverage = breakdowns.map(breakdown => {
    const step1Data = breakdown.steps?.step1 || {};
    const step1Count = step1Data.count || 0;
    return {
      ...breakdown,
      calculatedAverage: step1Count,
      breakdownKey: breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown'
    };
  });
  
  // Sort breakdowns
  const sortFn = (() => {
    switch (breakdownSortOrder) {
      case 'segment-a-z':
        return (a, b) => (a.breakdownKey || '').localeCompare(b.breakdownKey || '');
      case 'segment-z-a':
        return (a, b) => (b.breakdownKey || '').localeCompare(a.breakdownKey || '');
      case 'average-asc':
        return (a, b) => a.calculatedAverage - b.calculatedAverage;
      case 'average-desc':
      default:
        return (a, b) => b.calculatedAverage - a.calculatedAverage;
    }
  })();
  
  breakdownsWithAverage.sort(sortFn);
  
  // Limit to maxLimit
  return breakdownsWithAverage.slice(0, maxLimit);
}

// Build funnel line-chart data: conversion % + users + day-over-day delta per day
function buildFunnelLineData(funnelData) {
  const steps = funnelData?.steps || [];
  const timeSeries = funnelData?.timeSeries || [];

  if (!steps.length || steps.length < 2 || !timeSeries.length) {
    return {
      points: [],
      title: "Funnel Conversion Rate",
    };
  }

  const firstKey = 'step1';
  const lastKey = `step${steps.length}`;
  const firstLabel = steps[0]?.label || steps[0]?.eventName || 'Step 1';
      const lastLabel = steps[steps.length - 1]?.label || steps[steps.length - 1]?.eventName || `Step ${steps.length}`;

  const points = [];
  let prevConversion = null;

  timeSeries.forEach((row) => {
    const values = row.values || {};
    const first = values[firstKey] != null ? values[firstKey] : 0;
    const last = values[lastKey] != null ? values[lastKey] : 0;
    const conversion = first > 0 ? (last / first) * 100 : 0;
    const delta =
      prevConversion != null
        ? conversion - prevConversion
        : null;

    points.push({
      date: row.date,
      conversion,
      users: last,
      delta,
    });

    prevConversion = conversion;
  });

  return {
    points,
    title: `Conversion Rate of ${firstLabel} through ${lastLabel} \u2013 All Steps`,
  };
}

// Maps EventsTimeFilter preset keys to analytics preset names
const EVENTS_PRESET_TO_ANALYTICS = {
  today: "Today",
  yesterday: "Yesterday",
  "7d": "7D",
  "30d": "30D",
  "3m": "3M",
  "6m": "6M",
  "12m": "12M",
};

/** Build timeRange for buildInsightsQuery from insight time filter or global preset */
function getInsightTimeRange(insightTimeFilter, globalTimeRange) {
  if (insightTimeFilter && insightTimeFilter.type === "preset" && insightTimeFilter.preset) {
    // Use the named preset so the backend can cache by preset key and apply relative date logic
    const preset = EVENTS_PRESET_TO_ANALYTICS[insightTimeFilter.preset] || globalTimeRange || "30D";
    return {
      preset,
      start: null,
      end: null,
      interval: "day",
      timezone: "UTC",
    };
  }
  if (insightTimeFilter && insightTimeFilter.from && insightTimeFilter.to) {
    // Custom date range
    const { start, end } = normalizeCustomRangeToUtcDays(
      insightTimeFilter.from,
      insightTimeFilter.to,
      insightTimeFilter.enableTimeRanges === true
    );
    return {
      preset: null,
      start,
      end,
      interval: "day",
      timezone: "UTC",
    };
  }
  return {
    preset: globalTimeRange || "30D",
    start: null,
    end: null,
    interval: "day",
    timezone: "UTC",
  };
}

// Simple editor for Text or Media cards (edit content/url in place)
function TextOrMediaCardEditor({ team, boardId, cardId, editorType, card, cards, updateCard, theme, boardName = "" }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(card?.title || "Untitled");
  const [content, setContent] = useState(
    editorType === "text" ? (card?.queryDsl?.content ?? "") : ""
  );
  const [url, setUrl] = useState(editorType === "media" ? (card?.queryDsl?.url ?? "") : "");
  const [mediaType, setMediaType] = useState(editorType === "media" ? (card?.queryDsl?.type || "image") : "image");
  const [saving, setSaving] = useState(false);
  const breadcrumbs = [
    "Boards",
    { label: boardName || "Board", path: `/${team}/analytics/boards/${boardId}` },
    title || (editorType === "text" ? "Text" : "Media"),
  ];

  useEffect(() => {
    if (card) {
      setTitle(card.title || "Untitled");
      if (editorType === "text") setContent(card.queryDsl?.content ?? "");
      if (editorType === "media") {
        setUrl(card.queryDsl?.url ?? "");
        setMediaType(card.queryDsl?.type || "image");
      }
    }
  }, [card, editorType]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates =
        editorType === "text"
          ? { title: title.trim() || "Untitled", queryDsl: { content: content } }
          : { title: title.trim() || "Untitled", queryDsl: { url: url.trim(), type: mediaType } };
      await updateCard(cardId, updates);
      navigate(`/${team}/analytics/boards/${boardId}`);
    } catch (err) {
      console.error("Failed to update card:", err);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate(`/${team}/analytics/boards/${boardId}`);
  };

  if (!card && cards.length > 0) {
    return (
      <div className="flex flex-1 items-center justify-center" style={{ color: theme.textMuted }}>
        Card not found
      </div>
    );
  }

  if (!card) {
    return (
      <div className="flex flex-1 items-center justify-center" style={{ color: theme.textMuted }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "#ffffff" }}>
      <AnalyticsTopBar
        breadcrumbs={breadcrumbs}
        showCreateNew={false}
        showTimeRange={false}
        showSaveClose={true}
        onSave={handleSave}
        onClose={handleClose}
      />
      <main className="flex-1 overflow-auto p-6" style={{ fontFamily: "Raleway, sans-serif" }}>
        <div className="max-w-2xl space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              style={{ borderColor: theme.border, backgroundColor: theme.bgRaised, color: theme.textPrimary }}
            />
          </div>
          {editorType === "text" && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border rounded-lg resize-y"
                style={{ borderColor: theme.border, backgroundColor: theme.bgRaised, color: theme.textPrimary }}
                placeholder="Use text to tell a story..."
              />
            </div>
          )}
          {editorType === "media" && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: theme.border, backgroundColor: theme.bgRaised, color: theme.textPrimary }}
                  placeholder="Image or video URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: theme.textSecondary }}>Type</label>
                <select
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  className="px-3 py-2 border rounded-lg"
                  style={{ borderColor: theme.border, backgroundColor: theme.bgRaised, color: theme.textPrimary }}
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default function InsightsEditorPage() {
  const navigate = useNavigate();
  const { team, boardId } = useParams();
  const [searchParams] = useSearchParams();
  const editorType = searchParams.get("type") || "insights";
  const reportType = editorType === "funnels" ? "funnel" : editorType === "retention" ? "retention" : "insight";
  const cardIdParam = searchParams.get("cardId");
  const rowIndexParam = searchParams.get("rowIndex");
  const rowIndex = rowIndexParam != null && rowIndexParam !== "" ? parseInt(rowIndexParam, 10) : undefined;
  const selectedTeam = JSON.parse(localStorage.getItem("selectedTeamData") || "null");
  const teamId = selectedTeam?._id ?? selectedTeam?.id ?? null;
  const { globalTimeRange, setGlobalTimeRange, TIME_RANGES } = useAnalytics();
  const { result, loading, error, execute } = useInsights();
  const { events, loadEvents } = useEventsMetadata();
  const { createBoard } = useBoards();
  const { cards, loadCards, updateCard } = useCards(boardId, teamId);
  const editingCard = cardIdParam && cards.find((c) => String(c.id) === String(cardIdParam));
  const [boardName, setBoardName] = useState("");

  /** Same shape as Events page: { type: 'preset'|'custom', preset?, from, to }. When set, from/to are used in queryDSL. */
  const [insightTimeFilter, setInsightTimeFilter] = useState(null);

  const [activeTab, setActiveTab] = useState("query");
  // New metrics array structure (insight)
  const [metrics, setMetrics] = useState([]);
  // Funnel steps (funnel report type)
  const [funnelSteps, setFunnelSteps] = useState([]);
  const [funnelMeasuring, setFunnelMeasuring] = useState("conversion_rate");
  const [funnelMeasuringProperty, setFunnelMeasuringProperty] = useState(null);
  const [funnelMeasuringAggregation, setFunnelMeasuringAggregation] = useState("average");
  const [funnelMeasuringPercentileValue, setFunnelMeasuringPercentileValue] = useState(50);
  const [funnelConversionWindow, setFunnelConversionWindow] = useState({ value: 7, unit: 'days' });
  // Retention (retention report type)
  const [retentionStartEvent, setRetentionStartEvent] = useState("");
  const [retentionReturnEvent, setRetentionReturnEvent] = useState("");
  const [retentionInterval, setRetentionInterval] = useState("day");
  const [retentionRule, setRetentionRule] = useState("on_or_after");
  const [retentionMetric, setRetentionMetric] = useState("retention_rate");
  // Legacy state (for backward compatibility during migration)
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAggregation, setSelectedAggregation] = useState("count");
  const [metricFilters, setMetricFilters] = useState([]);
  const [globalFilters, setGlobalFilters] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]); // New: array of breakdowns
  // Breakdown sorting and limiting state
  const [breakdownSortOrder, setBreakdownSortOrder] = useState('average-desc'); // 'segment-a-z', 'segment-z-a', 'average-asc', 'average-desc'
  const [breakdownLimit, setBreakdownLimit] = useState(10);
  const [breakdownShowMax, setBreakdownShowMax] = useState(false);
  const [isBreakdownLimitDropdownOpen, setIsBreakdownLimitDropdownOpen] = useState(false);
  const [breakdownCustomLimitInput, setBreakdownCustomLimitInput] = useState('10');
  const breakdownLimitDropdownRef = useRef(null);
  const [chartType, setChartType] = useState(
    reportType === "funnel" ? "funnel-steps" : 
    reportType === "retention" ? "retention-curve" : 
    "line"
  );
  const [cardTitle, setCardTitle] = useState("Untitled");
  const [chartConfig, setChartConfig] = useState(null); // axis, themeId, colors from Chart section
  const hydratedCardIdRef = useRef(null);

  // Load board name for breadcrumb (Boards / <Board name> / <Chart name>)
  useEffect(() => {
    if (!boardId || boardId === "new") {
      setBoardName("");
      return;
    }
    let cancelled = false;
    analyticsApi.fetchBoard(boardId, false, teamId).then((res) => {
      if (cancelled) return;
      const data = res?.data ?? res;
      setBoardName(data?.name || "Board");
    }).catch(() => {
      if (!cancelled) setBoardName("Board");
    });
    return () => { cancelled = true; };
  }, [boardId, teamId]);

  // Hydrate state from existing card when opening with cardId (so Edit opens the same card, not a new one)
  useEffect(() => {
    if (!cardIdParam || !editingCard) return;
    if (hydratedCardIdRef.current === cardIdParam) return;
    hydratedCardIdRef.current = cardIdParam;
    const q = editingCard.queryDsl && typeof editingCard.queryDsl === "object" ? editingCard.queryDsl : {};

    if (editorType === "funnels" && q.definition?.steps) {
      const stepName = (s) => (s.event != null ? String(s.event).trim() : (s.eventName != null ? String(s.eventName).trim() : ""));
      setFunnelSteps(
        (q.definition.steps || []).map((s, i) => ({
          id: s.id || `step-${i + 1}`,
          eventName: stepName(s) || "",
          label: (s.label ?? s.event ?? s.eventName ?? "").trim() || "",
          source: s.source || "event",
          filters: Array.isArray(s.filters) ? s.filters : [],
        }))
      );
      setCardTitle(editingCard.title || "Untitled");
      const vis = editingCard.visualization || q.visualization || {};
      setChartType(vis.chartType || "funnel-steps");
      if (vis.config && typeof vis.config === "object") setChartConfig(vis.config);
      const def = q.definition;
      if (def?.measuring) setFunnelMeasuring(def.measuring);
      if (def?.measuringProperty !== undefined) setFunnelMeasuringProperty(def.measuringProperty || null);
      if (def?.measuringAggregation) setFunnelMeasuringAggregation(def.measuringAggregation);
      if (typeof def?.measuringPercentileValue === "number") setFunnelMeasuringPercentileValue(def.measuringPercentileValue);
      if (def?.conversionWindow && typeof def.conversionWindow === "object" && def.conversionWindow.value != null && def.conversionWindow.unit)
        setFunnelConversionWindow({ value: def.conversionWindow.value, unit: def.conversionWindow.unit });
      const defFilters = def?.filters;
      if (defFilters && Array.isArray(defFilters) && defFilters.length > 0) {
        setGlobalFilters(defFilters.map((f, i) => ({
          id: f.id || `global-filter-${i}-${Date.now()}`,
          field: f.field ?? f.property,
          property: f.field ?? f.property,
          operator: f.operator,
          value: f.value,
          value2: f.value2,
          propertyType: f.propertyType,
          propertySource: f.propertySource,
          category: f.category || "global",
          listMode: f.listMode,
          listItemType: f.listItemType,
          propertyLabel: f.propertyLabel,
          connector: i === 0 ? "WHERE" : "AND",
        })));
      } else {
        setGlobalFilters([]);
      }
      // Load breakdowns (support both new array format and legacy single breakdown)
      const defBreakdowns = def?.breakdowns;
      if (defBreakdowns && Array.isArray(defBreakdowns) && defBreakdowns.length > 0) {
        setBreakdowns(defBreakdowns);
      } else if (q.definition?.breakdown) {
        // Legacy: convert single breakdown to array
        setBreakdowns([q.definition.breakdown]);
      } else {
        setBreakdowns([]);
      }
      // Hydrate breakdown display options (same as insights)
      if (typeof q.definition?.breakdownLimit === 'number' && q.definition.breakdownLimit >= 1) {
        setBreakdownLimit(q.definition.breakdownLimit);
        setBreakdownCustomLimitInput(String(q.definition.breakdownLimit));
      }
      if (q.definition?.breakdownSortOrder && ['segment-a-z', 'segment-z-a', 'average-asc', 'average-desc'].includes(q.definition.breakdownSortOrder)) {
        setBreakdownSortOrder(q.definition.breakdownSortOrder);
      }
      if (q.definition?.breakdownShowMax === true) {
        setBreakdownShowMax(true);
      }
      const t = q.time;
      if (t && t.start && t.end) setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
      else if (t && t.preset) setGlobalTimeRange(t.preset);
      
      // Hydrate measuring-related fields
      if (q.definition?.measuring) {
        setFunnelMeasuring(q.definition.measuring);
      }
      if (q.definition?.measuringProperty) {
        setFunnelMeasuringProperty(q.definition.measuringProperty);
      }
      if (q.definition?.measuringAggregation) {
        setFunnelMeasuringAggregation(q.definition.measuringAggregation);
      }
      if (q.definition?.measuringPercentileValue != null) {
        setFunnelMeasuringPercentileValue(q.definition.measuringPercentileValue);
      }
      if (q.definition?.conversionWindow) {
        setFunnelConversionWindow(q.definition.conversionWindow);
      }
      
      return;
    }

    if (editorType === "retention" && q.definition) {
      const def = q.definition;
      if (def.startEvent) setRetentionStartEvent(def.startEvent);
      if (def.returnEvent) setRetentionReturnEvent(def.returnEvent);
      if (def.retentionRule) setRetentionRule(def.retentionRule);
      if (def.metric) setRetentionMetric(def.metric);
      setCardTitle(editingCard.title || "Untitled");
      const vis = editingCard.visualization || q.visualization || {};
      setChartType(vis.chartType || "retention-curve");
      if (vis.config && typeof vis.config === "object") setChartConfig(vis.config);
      const defFilters = def?.filters;
      if (defFilters && Array.isArray(defFilters) && defFilters.length > 0) {
        setGlobalFilters(defFilters.map((f, i) => ({
          id: f.id || `global-filter-${i}-${Date.now()}`,
          field: f.field ?? f.property,
          property: f.field ?? f.property,
          operator: f.operator,
          value: f.value,
          value2: f.value2,
          propertyType: f.propertyType,
          propertySource: f.propertySource,
          category: f.category || "global",
          listMode: f.listMode,
          listItemType: f.listItemType,
          propertyLabel: f.propertyLabel,
          connector: i === 0 ? "WHERE" : "AND",
        })));
      } else {
        setGlobalFilters([]);
      }
      // Load breakdown (retention uses single breakdown, not array)
      if (def.breakdown) {
        setBreakdowns([def.breakdown]);
      } else {
        setBreakdowns([]);
      }
      const t = q.time;
      if (t?.interval && ["day", "week", "month"].includes(t.interval)) {
        setRetentionInterval(t.interval);
      }
      if (t && t.start && t.end) setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
      else if (t && t.preset) setGlobalTimeRange(t.preset);
      return;
    }

    if (editorType !== "insights") return;
    const defMetrics = q.definition?.metrics;
    if (defMetrics && Array.isArray(defMetrics) && defMetrics.length > 0) {
      setMetrics(
        defMetrics.map((m) => ({
          id: m.id || `m${Math.random().toString(36).slice(2, 9)}`,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: (m.metricName || m.metric_name || "").trim() || undefined,
          events: (m.events || []).map((ev) => ({
            name: ev.name || "",
            source: ev.eventType === "transaction" ? "transaction" : "event",
            filters: Array.isArray(ev.filters) ? ev.filters : [],
          })),
        }))
      );
    }
    setCardTitle(editingCard.title || "Untitled");
    // Hydrate global filters from saved query so they apply when report runs
    const defFilters = q.definition?.filters;
    if (defFilters && Array.isArray(defFilters) && defFilters.length > 0) {
      setGlobalFilters(defFilters.map((f, i) => ({
        id: f.id || `global-filter-${i}-${Date.now()}`,
        field: f.field ?? f.property,
        property: f.field ?? f.property,
        operator: f.operator,
        value: f.value,
        value2: f.value2,
        propertyType: f.propertyType,
        propertySource: f.propertySource,
        category: f.category || 'global',
        listMode: f.listMode,
        listItemType: f.listItemType,
        propertyLabel: f.propertyLabel,
        connector: i === 0 ? 'WHERE' : 'AND',
      })));
    } else {
      setGlobalFilters([]);
    }
    // Load breakdowns (support both new array format and legacy single breakdown)
    const defBreakdowns = q.definition?.breakdowns;
    if (defBreakdowns && Array.isArray(defBreakdowns) && defBreakdowns.length > 0) {
      setBreakdowns(defBreakdowns);
    } else if (q.definition?.breakdown) {
      // Legacy: convert single breakdown to array
      setBreakdowns([q.definition.breakdown]);
    } else {
      setBreakdowns([]);
    }
    // Hydrate breakdown display options (top N, sort order) so editor and board match
    if (typeof q.definition?.breakdownLimit === 'number' && q.definition.breakdownLimit >= 1) {
      setBreakdownLimit(q.definition.breakdownLimit);
      setBreakdownCustomLimitInput(String(q.definition.breakdownLimit));
    }
    if (q.definition?.breakdownSortOrder && ['segment-a-z', 'segment-z-a', 'average-asc', 'average-desc'].includes(q.definition.breakdownSortOrder)) {
      setBreakdownSortOrder(q.definition.breakdownSortOrder);
    }
    if (q.definition?.breakdownShowMax === true) {
      setBreakdownShowMax(true);
    }
    // Prefer top-level card.visualization (where we persist chart customization) over queryDsl.visualization
    const vis = editingCard.visualization || q.visualization || {};
    const ct = vis.chartType || "line";
    setChartType(ct);
    if (vis.config && typeof vis.config === "object") {
      setChartConfig(vis.config);
    }
    // Hydrate time filter from saved queryDsl so custom date range is restored
    const t = q.time;
    if (t && t.start && t.end) {
      setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
    } else if (t && t.preset) {
      setInsightTimeFilter(null);
      setGlobalTimeRange(t.preset);
    } else {
      setInsightTimeFilter(null);
    }
  }, [editorType, cardIdParam, editingCard, setGlobalTimeRange]);

  // If no teamId from localStorage, use team name from URL (backend can look it up)
  const teamIdOrName = teamId || team;
  
  const hasMetric =
    reportType === "funnel"
      ? funnelSteps.length > 0 && funnelSteps.some((s) => (s.eventName || "").trim())
      : reportType === "retention"
      ? (retentionStartEvent || "").trim() && (retentionReturnEvent || "").trim()
      : (metrics.length > 0 || !!selectedEvent);

  // Load events on mount
  useEffect(() => {
    if (loadEvents && teamIdOrName) {
      loadEvents(teamIdOrName).catch(err => {
        console.error('InsightsEditorPage: Error in loadEvents:', err);
      });
    }
  }, [loadEvents, teamIdOrName, team]);

  // Debounce execute to avoid 2–3 rapid runs when state settles (e.g. open editor, hydrate card, filter change)
  const executeDebounceMs = 400;
  const executeTimeoutRef = useRef(null);

  // Execute query when metrics or funnel steps change (single path: buildReportQuery + execute), debounced
  useEffect(() => {
    const timeRange = getInsightTimeRange(insightTimeFilter, globalTimeRange);
    const baseState = { timeRange, chartType, globalFilters, breakdowns, breakdownLimit, breakdownSortOrder, breakdownShowMax };

    let queryDSL = null;
    if (reportType === "funnel") {
      const validSteps = funnelSteps.filter((s) => (s.eventName || "").trim());
      if (validSteps.length === 0) return;
      queryDSL = analyticsApi.buildReportQuery("funnel", { ...baseState, funnelSteps: validSteps, measuring: funnelMeasuring, measuringProperty: funnelMeasuringProperty, measuringAggregation: funnelMeasuringAggregation, measuringPercentileValue: funnelMeasuringPercentileValue, conversionWindow: funnelConversionWindow });
    } else if (reportType === "retention") {
      if (!(retentionStartEvent || "").trim() || !(retentionReturnEvent || "").trim()) return;
      const retentionTimeRange = { ...timeRange, interval: retentionInterval || "day" };
      queryDSL = analyticsApi.buildReportQuery("retention", { 
        ...baseState,
        timeRange: retentionTimeRange,
        startEvent: retentionStartEvent.trim(),
        returnEvent: retentionReturnEvent.trim(),
        retentionRule: retentionRule,
        metric: retentionMetric,
        breakdown: breakdowns && breakdowns.length > 0 ? breakdowns[0] : null
      });
    } else if (metrics.length > 0) {
      const hasEvents = metrics.some((m) => m.events && m.events.length > 0);
      if (!hasEvents) return;
      const metricsWithEvents = metrics
        .filter((m) => m.events && m.events.length > 0)
        .map((m) => ({
          id: m.id,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: m.metricName,
          events: (m.events || []).map((ev) => ({
            name: ev.name,
            eventType: ev.source === "transaction" ? "transaction" : "event",
            filters: (ev.filters || []).map((f) => ({
              property: f.field ?? f.property,
              operator: f.operator,
              value: f.value,
              value2: f.value2,
              propertyType: f.propertyType,
              propertySource: f.propertySource,
              category: f.category,
              listMode: f.listMode,
              listItemType: f.listItemType,
            })),
          })),
        }));
      if (metricsWithEvents.length === 0) return;
      queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, metrics: metricsWithEvents });
    } else if (selectedEvent) {
      const filters = (metricFilters || []).map((f) => ({
        property: f.property ?? f.field,
        operator: f.operator,
        value: f.value,
        category: f.category || "event_properties",
        eventName: f.eventName || selectedEvent,
      }));
      queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, event: selectedEvent, aggregation: selectedAggregation, filters });
    }

    if (!queryDSL) return;

    // Debounce: wait for state to settle so we only run once instead of 2–3 times
    if (executeTimeoutRef.current) clearTimeout(executeTimeoutRef.current);
    executeTimeoutRef.current = setTimeout(() => {
      executeTimeoutRef.current = null;
      execute(queryDSL, teamIdOrName);
    }, executeDebounceMs);

    return () => {
      if (executeTimeoutRef.current) {
        clearTimeout(executeTimeoutRef.current);
        executeTimeoutRef.current = null;
      }
    };
  }, [reportType, funnelSteps, funnelMeasuring, funnelMeasuringProperty, funnelMeasuringAggregation, funnelMeasuringPercentileValue, funnelConversionWindow, retentionStartEvent, retentionReturnEvent, retentionInterval, retentionRule, retentionMetric, metrics, selectedEvent, selectedAggregation, globalTimeRange, insightTimeFilter, chartType, metricFilters, globalFilters, breakdowns, breakdownLimit, breakdownSortOrder, breakdownShowMax, execute, teamIdOrName]);

  // Close breakdown limit dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (breakdownLimitDropdownRef.current && !breakdownLimitDropdownRef.current.contains(event.target)) {
        setIsBreakdownLimitDropdownOpen(false);
      }
    };

    if (isBreakdownLimitDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isBreakdownLimitDropdownOpen]);

  const handleSave = async () => {
    if (reportType === "funnel") {
      if (funnelSteps.length === 0 || !funnelSteps.some((s) => (s.eventName || "").trim())) {
        alert("Please add at least one funnel step");
        return;
      }
    } else if (reportType === "retention") {
      if (!(retentionStartEvent || "").trim() || !(retentionReturnEvent || "").trim()) {
        alert("Please select both start event and return event");
        return;
      }
    } else if (metrics.length === 0 && !selectedEvent) {
      alert("Please add at least one metric");
      return;
    }

    try {
      const timeRange = getInsightTimeRange(insightTimeFilter, globalTimeRange);
      const baseState = { timeRange, chartType, globalFilters, breakdowns, breakdownLimit: breakdownShowMax ? 50 : breakdownLimit, breakdownSortOrder, breakdownShowMax };

      let queryDSL = null;
      if (reportType === "funnel") {
        queryDSL = analyticsApi.buildReportQuery("funnel", {
          ...baseState,
          funnelSteps: funnelSteps.filter((s) => (s.eventName || "").trim()),
          measuring: funnelMeasuring,
          measuringProperty: funnelMeasuringProperty,
          measuringAggregation: funnelMeasuringAggregation,
          measuringPercentileValue: funnelMeasuringPercentileValue,
          conversionWindow: funnelConversionWindow,
        });
      } else if (reportType === "retention") {
        const retentionTimeRange = { ...timeRange, interval: retentionInterval || "day" };
        queryDSL = analyticsApi.buildReportQuery("retention", {
          ...baseState,
          timeRange: retentionTimeRange,
          startEvent: retentionStartEvent.trim(),
          returnEvent: retentionReturnEvent.trim(),
          retentionRule: retentionRule,
          metric: retentionMetric,
          breakdown: breakdowns && breakdowns.length > 0 ? breakdowns[0] : null
        });
      } else if (metrics.length > 0) {
        const metricsForDsl = metrics.map((m) => ({
          id: m.id,
          aggregation: m.aggregation || "count",
          axis: m.axis || "left",
          metricName: m.metricName,
          events: (m.events || []).map((ev) => ({
            name: ev.name,
            eventType: ev.source === "transaction" ? "transaction" : "event",
            filters: (ev.filters || []).map((f) => ({
              property: f.field ?? f.property,
              operator: f.operator,
              value: f.value,
              value2: f.value2,
              propertyType: f.propertyType,
              propertySource: f.propertySource,
              category: f.category,
              listMode: f.listMode,
              listItemType: f.listItemType,
            })),
          })),
        }));
        queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, metrics: metricsForDsl });
      } else {
        const filters = (metricFilters || []).map((f) => ({
          property: f.property ?? f.field,
          operator: f.operator,
          value: f.value,
          category: f.category || "event_properties",
          eventName: f.eventName || selectedEvent,
        }));
        queryDSL = analyticsApi.buildReportQuery("insight", { ...baseState, event: selectedEvent, aggregation: selectedAggregation, filters });
      }

      const cardTypeForApi = reportType === "funnel" ? "funnel" : reportType === "retention" ? "retention" : "insights";

      // Editing existing card: update in place
      if (cardIdParam && boardId && editingCard) {
        await updateCard(cardIdParam, {
          title: cardTitle.trim() || "Untitled",
          queryDsl: queryDSL,
          visualization: { chartType, config: chartConfig || {} },
        });
        navigate(`/${team}/analytics/boards/${boardId}`);
        return;
      }

      // New card: create board if needed, then create card
      let targetBoardId = boardId;
      if (!targetBoardId || targetBoardId === "new") {
        const newBoard = await createBoard({
          name: cardTitle || "Untitled Board",
          description: "",
          time_range: { preset: globalTimeRange, interval: "day", timezone: "UTC" },
          filters: null,
          compare_mode: null,
          layout: null
        }, teamId);
        targetBoardId = String(newBoard.id);
      }

      const position =
        typeof rowIndex === "number"
          ? { x: 0, y: 0, w: 1, h: 1, rowIndex }
          : undefined;
      await analyticsApi.createCard(targetBoardId, {
        cardType: cardTypeForApi,
        title: cardTitle,
        queryDsl: queryDSL,
        visualization: {
          chartType,
          config: chartConfig || {}
        },
        ...(position && { position })
      }, teamId);

      navigate(`/${team}/analytics/boards/${targetBoardId}`);
    } catch (err) {
      console.error("Failed to save card:", err);
      alert("Failed to save card. Please try again.");
    }
  };

  const handleClose = () => {
    navigate(`/${team}/analytics/boards/${boardId || "new"}`);
  };

  const breadcrumbs = [
    "Boards",
    boardId && boardId !== "new"
      ? { label: boardName || "Board", path: `/${team}/analytics/boards/${boardId}` }
      : (boardName || "Board"),
    cardTitle || "Untitled",
  ];

  // Simple editor for Text or Media cards (when cardId is in URL)
  if ((editorType === "text" || editorType === "media") && cardIdParam && boardId) {
    return (
      <TextOrMediaCardEditor
        team={team}
        boardId={boardId}
        cardId={cardIdParam}
        editorType={editorType}
        card={editingCard}
        cards={cards}
        updateCard={updateCard}
        theme={theme}
        boardName={boardName}
      />
    );
  }

  return (
    <ReportLayout
      topBar={
        <AnalyticsTopBar
          breadcrumbs={breadcrumbs}
          showCreateNew={false}
          showTimeRange={false}
          showSaveClose={true}
          onSave={handleSave}
          onClose={handleClose}
        />
      }
      reportType={reportType}
      headerProps={{
        selectedPreset:
          insightTimeFilter
            ? insightTimeFilter.type === "custom"
              ? "custom"
              : (insightTimeFilter.preset || "30d")
            : analyticsPresetToEventsPreset(globalTimeRange),
        onTimeFilterChange: (filter) => {
          setInsightTimeFilter(filter);
          if (filter.type === "preset" && filter.preset) {
            const analyticsPreset =
              filter.preset === "today" ? "Today" : filter.preset === "yesterday" ? "Yesterday" : filter.preset === "7d" ? "7D" : filter.preset === "30d" ? "30D" : filter.preset === "3m" ? "3M" : filter.preset === "6m" ? "6M" : filter.preset === "12m" ? "12M" : globalTimeRange;
            setGlobalTimeRange(analyticsPreset);
          }
        },
        chartType,
        onChartTypeChange: setChartType,
        theme,
      }}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      rightPanel={
        <QueryBuilderPanel
          key={cardIdParam || "new"}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          reportType={reportType}
          funnelSteps={funnelSteps}
          onFunnelStepsChange={setFunnelSteps}
          funnelMeasuring={funnelMeasuring}
          onFunnelMeasuringChange={setFunnelMeasuring}
          funnelMeasuringProperty={funnelMeasuringProperty}
          onFunnelMeasuringPropertyChange={setFunnelMeasuringProperty}
          funnelMeasuringAggregation={funnelMeasuringAggregation}
          onFunnelMeasuringAggregationChange={setFunnelMeasuringAggregation}
          funnelMeasuringPercentileValue={funnelMeasuringPercentileValue}
          onFunnelMeasuringPercentileValueChange={setFunnelMeasuringPercentileValue}
          funnelConversionWindow={funnelConversionWindow}
          onFunnelConversionWindowChange={setFunnelConversionWindow}
          retentionStartEvent={retentionStartEvent}
          onRetentionStartEventChange={setRetentionStartEvent}
          retentionReturnEvent={retentionReturnEvent}
          onRetentionReturnEventChange={setRetentionReturnEvent}
          retentionInterval={retentionInterval}
          onRetentionIntervalChange={setRetentionInterval}
          retentionRule={retentionRule}
          onRetentionRuleChange={setRetentionRule}
          retentionMetric={retentionMetric}
          onRetentionMetricChange={setRetentionMetric}
          metrics={metrics}
          onMetricsChange={setMetrics}
          availableEvents={events}
          chartType={chartType}
          onChartTypeChange={setChartType}
          teamId={teamId}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          breakdowns={breakdowns}
          onBreakdownsChange={setBreakdowns}
          visualizationConfig={chartConfig}
          onVisualizationConfigChange={setChartConfig}
          retentionIntervals={result?.data?.intervals ?? result?.intervals ?? []}
          selectedEvent={selectedEvent}
          onEventSelect={setSelectedEvent}
          selectedAggregation={selectedAggregation}
          onAggregationSelect={setSelectedAggregation}
          metricFilters={metricFilters}
          onMetricFiltersChange={setMetricFilters}
        />
      }
      theme={theme}
    >
            {!hasMetric ? (
              <InsightsEmptyState
                onLearnBasics={() => {}}
                onExploreExamples={() => {}}
              />
            ) : loading ? (
              <div className="p-6 flex-1 flex items-center justify-center">
                <div className="text-sm" style={{ color: theme.textMuted }}>
                  Loading data...
                </div>
              </div>
            ) : error ? (
              <div className="p-6 flex-1 flex items-center justify-center">
                <div className="text-sm" style={{ color: "#ef4444" }}>
                  Error: {error}
                </div>
              </div>
            ) : reportType === "retention" && (result?.data || result?.intervals) ? (
              /* Retention result - render based on chartType */
              <div className="min-w-0 p-6">
                <div className="mb-4">
                  <input
                    type="text"
                    value={cardTitle}
                    onChange={(e) => setCardTitle(e.target.value)}
                    className="text-lg font-medium bg-transparent border-b outline-none"
                    style={{ color: theme.textPrimary, borderColor: theme.border, fontFamily: "Raleway, sans-serif" }}
                    placeholder="Card title"
                  />
                </div>
                {chartType === "metric" ? (
                  /* Metric view - show single retention rate */
                  <div className="w-full">
                    {(() => {
                      const avg = result?.data?.weightedAverage ?? result?.weightedAverage;
                      const intervals = result?.data?.intervals ?? result?.intervals ?? [];
                      const idx = chartConfig?.retentionLineIntervalIndex ?? 0;
                      const safeIdx =
                        Array.isArray(intervals) && intervals.length > 0
                          ? Math.min(Math.max(0, idx), intervals.length - 1)
                          : 0;
                      const groupLabel =
                        chartConfig?.retentionLineIntervalIndex != null && intervals?.[safeIdx]
                          ? intervals[safeIdx]
                          : "<1 Day";

                      let displayValue = retentionMetric === "retention_rate" ? "0%" : "0";
                      if (avg && Array.isArray(avg.values) && avg.values.length > 0) {
                        const selected = avg.values[safeIdx] ?? avg.values[0];
                        const rawNum = selected?.value ?? selected;
                        if (retentionMetric === "retention_rate") {
                          const num = Number(rawNum);
                          const pct = Number.isFinite(num) ? (num <= 1 ? num * 100 : num) : 0;
                          displayValue = `${pct.toFixed(2)}%`;
                        } else {
                          const users = Number(rawNum);
                          displayValue = Number.isFinite(users) ? formatNumber(users) : "0";
                        }
                      }

                      const baseColor =
                        (Array.isArray(chartConfig?.colors) && chartConfig.colors[0]) ||
                        theme.accentChart ||
                        theme.primary ||
                        "#7c3aed";

                      const hexToRgba = (hex, alpha) => {
                        const h = String(hex || "").replace("#", "").trim();
                        const full =
                          h.length === 3
                            ? h.split("").map((c) => c + c).join("")
                            : h.length === 6
                              ? h
                              : null;
                        if (!full) return `rgba(124, 58, 237, ${alpha})`;
                        const r = parseInt(full.slice(0, 2), 16);
                        const g = parseInt(full.slice(2, 4), 16);
                        const b = parseInt(full.slice(4, 6), 16);
                        if (![r, g, b].every((n) => Number.isFinite(n))) return `rgba(124, 58, 237, ${alpha})`;
                        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                      };

                      const hoverBg = hexToRgba(baseColor, 0.10);
                      const breakdowns = result?.data?.breakdowns ?? result?.breakdowns ?? [];
                      const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;

                      const breakdownPalette = [
                        "#7c3aed", "#3b82f6", "#10b981", "#f59e0b",
                        "#ef4444", "#8b5cf6", "#06b6d4", "#84cc16",
                      ];

                      const formatMetricDisplay = (rawNum) => {
                        if (retentionMetric === "retention_rate") {
                          const num = Number(rawNum);
                          const pctNum = Number.isFinite(num) ? (num <= 1 ? num * 100 : num) : 0;
                          return `${pctNum.toFixed(2)}%`;
                        }
                        const users = Number(rawNum);
                        return Number.isFinite(users) ? formatNumber(users) : "0";
                      };

                      return (
                        <>
                          {hasBreakdowns ? (
                            <div className="w-full rounded-xl mb-6" style={{ backgroundColor: "#ffffff", border: `1px solid ${theme.border}` }}>
                              <div className="p-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
                                <div className="text-xs" style={{ color: theme.textMuted }}>
                                  {`${retentionStartEvent || "Start Event"} then ${retentionReturnEvent || "Return Event"}`}
                                  {groupLabel ? ` • ${groupLabel}` : ""}
                                </div>
                              </div>

                              <div className="p-4 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                                {breakdowns.map((bd, i) => {
                                  const raw = Array.isArray(bd?.values) ? (bd.values[safeIdx] ?? bd.values[0]) : 0;
                                  const valueText = formatMetricDisplay(raw);
                                  const segLabel = bd?.segment || "(none)";
                                  const color = breakdownPalette[i % breakdownPalette.length] || baseColor;
                                  return (
                                    <div
                                      key={`${segLabel}-${i}`}
                                      className="rounded-lg p-4"
                                      style={{
                                        border: `1px solid ${theme.border}`,
                                        backgroundColor: "#ffffff",
                                      }}
                                    >
                                      <div className="text-xs mb-2" style={{ color: theme.textMuted }}>
                                        <span
                                          style={{
                                            display: "inline-block",
                                            width: 8,
                                            height: 8,
                                            borderRadius: 2,
                                            backgroundColor: color,
                                            marginRight: 8,
                                            verticalAlign: "middle",
                                          }}
                                        />
                                        <span style={{ verticalAlign: "middle" }}>{segLabel}</span>
                                      </div>
                                      <div className="text-4xl font-bold" style={{ color: theme.textPrimary }}>
                                        {valueText}
                                      </div>
                                      <div className="text-sm mt-1" style={{ color: theme.textMuted }}>
                                        {retentionMetric === "retention_rate" ? "Retention Rate" : "Unique Users"}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            /* Mixpanel-style metric panel */
                            <div
                              className="w-full flex items-center justify-center rounded-xl mb-6"
                              style={{
                                minHeight: "260px",
                                backgroundColor: "#ffffff",
                                border: `1px solid ${theme.border}`,
                                transition: "background-color 120ms ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffffff";
                              }}
                            >
                              <div className="text-center">
                                <div className="text-xs mb-2" style={{ color: theme.textMuted }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 8,
                                      height: 8,
                                      borderRadius: 2,
                                      backgroundColor: baseColor,
                                      marginRight: 8,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  <span style={{ verticalAlign: "middle" }}>
                                    {`${retentionStartEvent || "Start Event"} then ${retentionReturnEvent || "Return Event"}`}
                                  </span>
                                </div>
                                <div className="text-6xl font-bold" style={{ color: theme.primary }}>
                                  {displayValue}
                                </div>
                                <div className="text-sm mt-1" style={{ color: theme.textMuted }}>
                                  {retentionMetric === "retention_rate" ? "Retention Rate" : "Unique Users"}
                                  {groupLabel ? ` • ${groupLabel}` : ""}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Mixpanel-style table below metric */}
                          <RetentionMetricTable
                            label={`${retentionStartEvent || "Start Event"} then ${retentionReturnEvent || "Return Event"}`}
                            value={displayValue}
                            rows={
                              hasBreakdowns
                                ? breakdowns.map((bd) => ({
                                    label: bd?.segment || "(none)",
                                    value: formatMetricDisplay(
                                      Array.isArray(bd?.values) ? (bd.values[safeIdx] ?? bd.values[0]) : 0
                                    ),
                                  }))
                                : null
                            }
                          />
                        </>
                      );
                    })()}
                  </div>
                ) : chartType === "retention-curve" || chartType === "line" ? (
                  /* Retention curve vs calendar line chart + cohort table */
                  <RetentionVisualization
                    intervals={result?.data?.intervals ?? result?.intervals ?? []}
                    cohorts={result?.data?.cohorts ?? result?.cohorts ?? []}
                    weightedAverage={result?.data?.weightedAverage ?? result?.weightedAverage ?? null}
                    breakdowns={result?.data?.breakdowns ?? result?.breakdowns ?? []}
                    metric={retentionMetric}
                    config={chartConfig || {}}
                    chartType={chartType}
                    startEventLabel={retentionStartEvent}
                    returnEventLabel={retentionReturnEvent}
                  />
                ) : null}
              </div>
            ) : reportType === "funnel" && result?.data?.steps?.length > 0 ? (
              /* Funnel result - render either funnel steps view or line chart based on chartType */
              <div className="min-w-0 p-6">
                <div className="mb-4">
                  <input
                    type="text"
                    value={cardTitle}
                    onChange={(e) => setCardTitle(e.target.value)}
                    className="text-lg font-medium bg-transparent border-b outline-none"
                    style={{ color: theme.textPrimary, borderColor: theme.border, fontFamily: "Raleway, sans-serif" }}
                    placeholder="Card title"
                  />
                </div>

                {/* Breakdown Controls - only show when breakdowns exist */}
                {result.data.breakdowns && result.data.breakdowns.length > 0 && (
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Sort Dropdown */}
                      <select
                        value={breakdownSortOrder}
                        onChange={(e) => setBreakdownSortOrder(e.target.value)}
                        className="px-3 py-1.5 text-sm border rounded-lg cursor-pointer outline-none"
                        style={{
                          backgroundColor: theme.bgRaised,
                          borderColor: theme.border,
                          color: theme.textPrimary
                        }}
                      >
                        <option value="segment-a-z">Segment A → Z</option>
                        <option value="segment-z-a">Segment Z → A</option>
                        <option value="average-asc">Average 1 → 100</option>
                        <option value="average-desc">Average 100 → 1</option>
                      </select>

                      {/* Limit Dropdown */}
                      <div className="relative" ref={breakdownLimitDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsBreakdownLimitDropdownOpen(!isBreakdownLimitDropdownOpen)}
                          className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-2"
                          style={{
                            backgroundColor: theme.bgRaised,
                            borderColor: theme.border,
                            color: theme.textPrimary
                          }}
                        >
                          Top {breakdownShowMax ? 50 : breakdownLimit}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {isBreakdownLimitDropdownOpen && (
                          <div
                            className="absolute top-full left-0 mt-1 p-4 rounded-lg border shadow-lg z-[100] min-w-[280px]"
                            style={{
                              backgroundColor: theme.bgRaised,
                              borderColor: theme.border
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                              Top Segments
                            </div>
                            <div className="text-xs mb-3" style={{ color: theme.textMuted }}>
                              Choose the number of rows you want to see in the chart, up to 50.
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                              <input
                                type="number"
                                min="1"
                                max="50"
                                value={breakdownCustomLimitInput}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setBreakdownCustomLimitInput(val);
                                  const num = parseInt(val, 10);
                                  if (!isNaN(num) && num >= 1 && num <= 50) {
                                    setBreakdownLimit(num);
                                    setBreakdownShowMax(false);
                                  }
                                }}
                                onBlur={() => {
                                  const num = parseInt(breakdownCustomLimitInput, 10);
                                  if (isNaN(num) || num < 1) {
                                    setBreakdownCustomLimitInput(breakdownLimit.toString());
                                  } else if (num > 50) {
                                    setBreakdownCustomLimitInput('50');
                                    setBreakdownLimit(50);
                                    setBreakdownShowMax(false);
                                  } else {
                                    setBreakdownLimit(num);
                                    setBreakdownShowMax(false);
                                  }
                                }}
                                className="flex-1 px-2 py-1.5 text-sm border rounded"
                                style={{
                                  backgroundColor: theme.bgBase,
                                  borderColor: theme.border,
                                  color: theme.textPrimary
                                }}
                              />
                              <span className="text-sm" style={{ color: theme.textMuted }}>segments</span>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={breakdownShowMax}
                                onChange={(e) => {
                                  setBreakdownShowMax(e.target.checked);
                                  if (e.target.checked) {
                                    setBreakdownLimit(50);
                                    setBreakdownCustomLimitInput('50');
                                  }
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm" style={{ color: theme.textPrimary }}>
                                Show max segments (up to 50)
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Funnel Chart / Metric Area */}
                <div className="mb-8">
                  {(() => {
                    // Sort and limit breakdowns for all chart types
                    const sortedBreakdowns = sortAndLimitFunnelBreakdowns(
                      result.data.breakdowns || [],
                      breakdownLimit,
                      breakdownSortOrder,
                      breakdownShowMax
                    );

                    if (chartType === "metric") {
                      return (
                        <FunnelMetricView
                          steps={result.data.steps.map(step => ({
                            ...step,
                            measuring:
                              step.measuring || funnelMeasuring || "conversion_rate",
                          }))}
                          breakdowns={sortedBreakdowns}
                          measuring={funnelMeasuring || "conversion_rate"}
                          config={chartConfig ?? DEFAULT_FUNNEL_CHART_CONFIG}
                        />
                      );
                    } else if (chartType === "line" && (result.data.timeSeriesBreakdowns?.length || result.data.timeSeries?.length)) {
                      const lineMeta = buildFunnelLineData(result.data);
                      return (
                        <FunnelLineChart
                          points={
                            result.data.timeSeriesBreakdowns?.length
                              ? []
                              : lineMeta.points
                          }
                          title={lineMeta.title}
                          theme={theme}
                          height={320}
                          breakdownSegments={
                            result.data.timeSeriesBreakdowns || null
                          }
                          measuring={funnelMeasuring || "conversion_rate"}
                          config={chartConfig ?? DEFAULT_FUNNEL_CHART_CONFIG}
                        />
                      );
                    } else if (chartType === "bar") {
                      if (result.data.breakdowns?.length) {
                        return (
                          <FunnelBreakdownBarChart
                            steps={result.data.steps.map((step) => ({
                              ...step,
                              measuring:
                                step.measuring ||
                                funnelMeasuring ||
                                "conversion_rate",
                            }))}
                            breakdowns={sortedBreakdowns}
                            measuring={funnelMeasuring || "conversion_rate"}
                            config={chartConfig ?? DEFAULT_FUNNEL_CHART_CONFIG}
                          />
                        );
                      } else {
                        return (
                          <FunnelSummaryBarChart
                            steps={result.data.steps.map(step => ({
                              ...step,
                              measuring:
                                step.measuring || funnelMeasuring || "conversion_rate",
                            }))}
                            config={chartConfig ?? DEFAULT_FUNNEL_CHART_CONFIG}
                          />
                        );
                      }
                    } else {
                      return (
                        <FunnelVisualization
                          steps={result.data.steps.map(step => ({
                            ...step,
                            measuring:
                              step.measuring || funnelMeasuring || "conversion_rate",
                          }))}
                          measuring={funnelMeasuring || "conversion_rate"}
                          overallConversionRate={
                            result.data.steps.length > 1
                              ? result.data.steps[result.data.steps.length - 1]
                                  ?.conversion
                              : 100
                          }
                          breakdowns={sortedBreakdowns}
                          config={chartConfig ?? DEFAULT_FUNNEL_CHART_CONFIG}
                        />
                      );
                    }
                  })()}
                </div>

                {/* Funnel Data Table (always shown) */}
                <div className="mt-8">
                  <FunnelDataTable
                    steps={result.data.steps.map(step => ({
                      ...step,
                      measuring:
                        step.measuring || funnelMeasuring || "conversion_rate",
                    }))}
                    measuring={funnelMeasuring || "conversion_rate"}
                    breakdowns={sortAndLimitFunnelBreakdowns(
                      result.data.breakdowns || [],
                      breakdownLimit,
                      breakdownSortOrder,
                      breakdownShowMax
                    )}
                    config={chartConfig ?? DEFAULT_FUNNEL_CHART_CONFIG}
                  />
                </div>
              </div>
            ) : result && result.series && result.series.length > 0 ? (
              <div className="min-w-0">
                {(() => {
                  // Helper function to normalize, sort, and limit breakdowns
                  // When multiple metrics: sort and limit PER METRIC (each metric gets its own top N segments)
                  const normalizeSortAndLimitBreakdowns = (resultSeries, hasMultipleMetrics) => {
                    if (!resultSeries || resultSeries.length === 0) return [];
                    
                    // Check if this is breakdown format
                    const isBreakdownFormat = resultSeries[0].breakdowns && 
                                              Array.isArray(resultSeries[0].breakdowns) &&
                                              resultSeries[0].breakdowns.length > 0;
                    
                    if (!isBreakdownFormat) return resultSeries;
                    
                    const maxLimit = breakdownShowMax ? 50 : Math.min(breakdownLimit, 50);
                    const sortFn = (() => {
                      switch (breakdownSortOrder) {
                        case 'segment-a-z': return (a, b) => (a.originalBreakdownKey || '').localeCompare(b.originalBreakdownKey || '');
                        case 'segment-z-a': return (a, b) => (b.originalBreakdownKey || '').localeCompare(a.originalBreakdownKey || '');
                        case 'average-asc': return (a, b) => a.calculatedAverage - b.calculatedAverage;
                        case 'average-desc':
                        default: return (a, b) => b.calculatedAverage - a.calculatedAverage;
                      }
                    })();

                    const allSeries = [];
                    // Process each metric separately so breakdown segments are unique per metric
                    resultSeries.forEach((metricSeries, metricIndex) => {
                      if (!metricSeries.breakdowns || !Array.isArray(metricSeries.breakdowns)) return;
                      const metricIdentifier = String.fromCharCode(65 + metricIndex);
                      const metricSegments = [];
                      metricSeries.breakdowns.forEach((breakdown) => {
                        const breakdownValueLabel = breakdown.breakdownKey || 
                          (breakdown.breakdownValues && breakdown.breakdownValues.join(' / ')) ||
                          metricSeries.metricName;
                        // Header display: "Country" or "India/Gurugram" for hierarchical
                        const displayLabel = breakdown.breakdownValues && breakdown.breakdownValues.length > 0
                          ? breakdown.breakdownValues.join('/')
                          : (breakdown.breakdownKey || breakdownValueLabel);
                        const dataPoints = breakdown.data || [];
                        const sum = dataPoints.reduce((acc, point) => acc + (point.value || 0), 0);
                        const calculatedAverage = dataPoints.length > 0 ? sum / dataPoints.length : 0;
                        const segmentTotal = breakdown.total ?? sum;
                        metricSegments.push({
                          metricId: metricSeries.metricId,
                          metricName: displayLabel,
                          metricIdentifier,
                          displayLabel,
                          fullMetricName: metricSeries.metricName,
                          data: breakdown.data || [],
                          summary: {
                            ...(metricSeries.summary || {}),
                            total: segmentTotal,
                            average: calculatedAverage
                          },
                          breakdownKey: breakdown.breakdownKey,
                          breakdownValues: breakdown.breakdownValues,
                          breakdownDimensionNames: metricSeries.breakdownDimensionNames, // Preserve dimension names
                          calculatedAverage: calculatedAverage,
                          originalBreakdownKey: breakdownValueLabel
                        });
                      });
                      metricSegments.sort(sortFn);
                      allSeries.push(...metricSegments.slice(0, maxLimit));
                    });
                    return allSeries;
                  };

                  // Check if this is breakdown format
                  const isBreakdownFormat = result.series.length > 0 && 
                                            result.series[0].breakdowns && 
                                            Array.isArray(result.series[0].breakdowns) &&
                                            result.series[0].breakdowns.length > 0;
                  
                  const hasMultipleMetrics = result.series.length > 1;
                  const transformedSeries = isBreakdownFormat 
                    ? normalizeSortAndLimitBreakdowns(result.series, hasMultipleMetrics)
                    : result.series;

                  return (
                    <>
                      <div className="mb-4 flex items-center gap-4 pt-6 px-6">
                        <input
                          type="text"
                          value={cardTitle}
                          onChange={(e) => setCardTitle(e.target.value)}
                          className="text-lg font-medium bg-transparent border-b outline-none"
                          style={{ 
                            color: theme.textPrimary,
                            borderColor: theme.border,
                            fontFamily: "Raleway, sans-serif"
                          }}
                          placeholder="Card title"
                        />
                      </div>
                      
                      {/* Breakdown Controls - only show for breakdown format */}
                      {isBreakdownFormat && (
                        <div className="mb-4 flex items-center justify-between px-6">
                          <div className="flex items-center gap-3">
                            {/* Sort Dropdown */}
                            <select
                              value={breakdownSortOrder}
                              onChange={(e) => setBreakdownSortOrder(e.target.value)}
                              className="px-3 py-1.5 text-sm border rounded-lg cursor-pointer outline-none"
                              style={{
                                backgroundColor: theme.bgRaised,
                                borderColor: theme.border,
                                color: theme.textPrimary
                              }}
                            >
                              <option value="segment-a-z">Segment A → Z</option>
                              <option value="segment-z-a">Segment Z → A</option>
                              <option value="average-asc">Average 1 → 100</option>
                              <option value="average-desc">Average 100 → 1</option>
                            </select>

                            {/* Limit Dropdown */}
                            <div className="relative" ref={breakdownLimitDropdownRef}>
                              <button
                                type="button"
                                onClick={() => setIsBreakdownLimitDropdownOpen(!isBreakdownLimitDropdownOpen)}
                                className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-2"
                                style={{
                                  backgroundColor: theme.bgRaised,
                                  borderColor: theme.border,
                                  color: theme.textPrimary
                                }}
                              >
                                Top {breakdownShowMax ? 50 : breakdownLimit}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>

                              {isBreakdownLimitDropdownOpen && (
                                <div
                                  className="absolute top-full left-0 mt-1 p-4 rounded-lg border shadow-lg z-[100] min-w-[280px]"
                                  style={{
                                    backgroundColor: theme.bgRaised,
                                    borderColor: theme.border
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>
                                    Top Segments
                                  </div>
                                  <div className="text-xs mb-3" style={{ color: theme.textMuted }}>
                                    Choose the number of rows you want to see in the chart, up to 50.
                                  </div>
                                  <div className="flex items-center gap-2 mb-3">
                                    <input
                                      type="number"
                                      min="1"
                                      max="50"
                                      value={breakdownCustomLimitInput}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setBreakdownCustomLimitInput(val);
                                        const num = parseInt(val, 10);
                                        if (!isNaN(num) && num >= 1 && num <= 50) {
                                          setBreakdownLimit(num);
                                          setBreakdownShowMax(false);
                                        }
                                      }}
                                      onBlur={() => {
                                        const num = parseInt(breakdownCustomLimitInput, 10);
                                        if (isNaN(num) || num < 1) {
                                          setBreakdownCustomLimitInput(breakdownLimit.toString());
                                        } else if (num > 50) {
                                          setBreakdownCustomLimitInput('50');
                                          setBreakdownLimit(50);
                                          setBreakdownShowMax(false);
                                        } else {
                                          setBreakdownLimit(num);
                                          setBreakdownShowMax(false);
                                        }
                                      }}
                                      className="flex-1 px-2 py-1.5 text-sm border rounded"
                                      style={{
                                        backgroundColor: theme.bgBase,
                                        borderColor: theme.border,
                                        color: theme.textPrimary
                                      }}
                                    />
                                    <span className="text-sm" style={{ color: theme.textMuted }}>segments</span>
                                  </div>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={breakdownShowMax}
                                      onChange={(e) => {
                                        setBreakdownShowMax(e.target.checked);
                                        if (e.target.checked) {
                                          setBreakdownLimit(50);
                                          setBreakdownCustomLimitInput('50');
                                        }
                                      }}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-sm" style={{ color: theme.textPrimary }}>
                                      Show max segments (up to 50)
                                    </span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                {result.series.length === 1 ? (
                  <div className="mb-4 flex items-center gap-2 px-6">
                    <span className="text-sm" style={{ color: theme.textMuted }}>
                      {result.series[0].metricName}
                    </span>
                    {result.series[0].summary && (
                      <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                        Total: {formatNumber(result.series[0].summary.total)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 flex items-center gap-2 flex-wrap px-6">
                    {result.series.map((s, idx) => (
                      <span key={idx} className="text-sm" style={{ color: theme.textMuted }}>
                        {s.metricName}
                        {s.summary && ` (Total: ${formatNumber(s.summary.total)})`}
                        {idx < result.series.length - 1 && <span className="mx-2">•</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* Metric view: show metric cards (one per metric or one per breakdown segment when breakdown applied) */}
                {chartType === "metric" && (
                  <div className="px-6 pb-4">
                    <MetricCards
                      series={transformedSeries}
                      seriesColors={chartConfig?.colors}
                    />
                  </div>
                )}

                {/* Chart or Table: when chartType === "table", render ONLY the metric table (no chart, no lower table) */}
                {chartType !== "metric" && (
                  <div
                    className="pt-2 pb-4 min-h-[320px]"
                    style={{ backgroundColor: "transparent", margin: 0, paddingLeft: 0, paddingRight: 0 }}
                  >
                    {chartType === "table" ? (
                      /* Table chart: single table only. With breakdown → GroupedFlatTable; without → InsightsChart Metric|Total */
                      isBreakdownFormat ? (
                        (() => {
                          const breakdownProperties = result.series[0]?.breakdownProperties;
                          const fullTotalsByMetric = result.series.reduce((acc, s) => {
                            const id = s.metricId ?? s.metricName;
                            if (id != null && s.summary?.total != null) acc[id] = s.summary.total;
                            return acc;
                          }, {});
                          return (
                            <InsightsDataTable
                              allSeries={transformedSeries}
                              seriesColors={chartConfig?.colors}
                              breakdownProperties={breakdownProperties}
                              breakdownFormat={true}
                              chartType="table"
                              fullTotalsByMetric={fullTotalsByMetric}
                            />
                          );
                        })()
                      ) : (
                        <InsightsChart
                          data={{
                            series: transformedSeries,
                            metrics,
                            breakdownProperties: isBreakdownFormat ? (result.series[0]?.breakdownProperties || null) : null,
                            summary: result.series.length === 1 ? {
                              avg: result.series[0].summary?.average ?? result.series[0].summary?.avg,
                              total: result.series[0].summary?.total,
                              delta: result.series[0].delta != null
                                ? `${result.series[0].delta.percentage >= 0 ? '+' : ''}${Number(result.series[0].delta.percentage).toFixed(1)}%`
                                : undefined,
                              metric: result.series[0].metricName,
                            } : undefined,
                          }}
                          chartType="table"
                          config={chartConfig}
                          height={320}
                        />
                      )
                    ) : (
                      /* Non-table: show chart */
                      <InsightsChart
                        data={{
                          series: transformedSeries,
                          metrics,
                          breakdownProperties: isBreakdownFormat ? (result.series[0]?.breakdownProperties || null) : null,
                          summary: result.series.length === 1 ? {
                            avg: result.series[0].summary?.average ?? result.series[0].summary?.avg,
                            total: result.series[0].summary?.total,
                            delta: result.series[0].delta != null
                              ? `${result.series[0].delta.percentage >= 0 ? '+' : ''}${Number(result.series[0].delta.percentage).toFixed(1)}%`
                              : undefined,
                            metric: result.series[0].metricName,
                          } : undefined,
                        }}
                        chartType={chartType}
                        config={chartConfig}
                        height={320}
                      />
                    )}
                  </div>
                )}

                {/* Data table: only when chartType !== "table" (table chart shows single table above) */}
                {chartType !== "metric" && chartType !== "table" && (
                  <div className="pb-6">
                    {(() => {
                      if (isBreakdownFormat) {
                        const breakdownProperties = result.series[0]?.breakdownProperties;
                        return (
                          <InsightsDataTable
                            allSeries={transformedSeries}
                            seriesColors={chartConfig?.colors}
                            breakdownProperties={breakdownProperties}
                            breakdownFormat={true}
                          />
                        );
                      } else if (result.series.length === 1 && result.series[0].data) {
                        return (
                          <InsightsDataTable
                            series={result.series[0].data || []}
                            metricName={result.series[0].metricName}
                            average={result.series[0].summary?.average ?? result.series[0].summary?.avg}
                            seriesColors={chartConfig?.colors}
                          />
                        );
                      } else {
                        return (
                          <InsightsDataTable
                            allSeries={result.series}
                            seriesColors={chartConfig?.colors}
                          />
                        );
                      }
                    })()}
                  </div>
                )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="p-6 flex-1 flex items-center justify-center">
                <div className="text-sm" style={{ color: theme.textMuted }}>
                  No data available
                </div>
              </div>
            )}
    </ReportLayout>
  );
}
